"use client";

import Copyright from "../shared/Copyright";
import {
  alpha,
  Alert,
  Box,
  Button,
  FormControl,
  styled,
  Link,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import PasswordVisibilityToggle from "@components/shared/form/PasswordVisibilityToggle";
import Logo from "../shared/Logo";
import CookieConsent from "../shared/CookieConsent";
import { useEffect, useState } from "react";
import { signIn, getSession } from "next-auth/react";
import { useRouter } from "next/navigation";

/**
 * Read a post-login redirect target from `?callbackUrl=`, restricted to
 * same-app relative paths (must start with a single "/") so it can't be used
 * as an open redirect. Used by the OAuth connector consent flow to return the
 * user to `/oauth/authorize` after signing in.
 */
function safeCallbackUrl() {
  if (typeof window === "undefined") return "/";
  const target = new URLSearchParams(window.location.search).get("callbackUrl");
  if (target && target.startsWith("/") && !target.startsWith("//")) {
    return target;
  }
  return "/";
}
import Label from "@components/shared/form/Label";
import TextField from "@components/shared/form/TextField";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GoogleIcon from "@mui/icons-material/Google";
import { requestPasswordReset } from "@/lib/api/resetPassword";
import config from "@lib/config";
import { ACCOUNT_STATUS } from "@lib/enums";

const Container = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: "100vh",
  overflowY: "auto",
  padding: "48px 16px",
  boxSizing: "border-box",
});

const Content = styled(Box)({
  width: "100%",
  maxWidth: "600px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  rowGap: "34px",
});

const Form = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "40px",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  backgroundColor: alpha(theme.palette.primary.light, 0.17),
  overflow: "hidden",
  borderRadius: "6px",
}));

const Fields = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const PinkPill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  right: "-50px",
  width: "180px",
  height: "83px",
  borderRadius: "9999px",
  backgroundColor: alpha(theme.palette.secondary.main, 0.9),
}));

const BluePill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "70px",
  right: "-30px",
  width: "90px",
  height: "66px",
  borderRadius: "9999px",
  backgroundColor: alpha(theme.palette.primary.light, 0.7),
}));

const LoginField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },

  "input[data-com-onepassword-filled='light']": {
    backgroundColor: "transparent !important",
  },
}));

const LinkedInButton = styled(Button)(({ theme }) => ({
  width: "100%",
  marginTop: "24px",
  marginBottom: "24px",
  backgroundColor: "#fff",
  color: "#0077B5",
  border: "2px solid #0077B5",
  "&:hover": {
    backgroundColor: "#f3f9ff",
    border: "2px solid #006097",
  },
  textTransform: "none",
  display: "flex",
  gap: "12px",
  padding: "12px 16px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "8px",
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  width: "100%",
  marginBottom: "24px",
  backgroundColor: "#fff",
  color: "#4285F4",
  border: "2px solid #4285F4",
  "&:hover": {
    backgroundColor: "#f0f4ff",
    border: "2px solid #3367D6",
  },
  textTransform: "none",
  display: "flex",
  gap: "12px",
  padding: "12px 16px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "8px",
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },
}));

const OrDivider = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  marginBottom: "24px",
  "& .MuiDivider-root": {
    flexGrow: 1,
  },
  "& .MuiTypography-root": {
    margin: "0 16px",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}));

const Mode = {
  LOGIN: "login",
  FORGOT_PASSWORD: "forgot-password",
  INACTIVE: "inactive",
};

export default function Login() {
  const [mode, setMode] = useState(Mode.LOGIN);
  const [inactiveMessage, setInactiveMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  useEffect(() => {
    // Handle URL parameters. This reads browser-only `window.location.search`
    // and then strips the query string, so it must run after mount. Starting
    // with empty state keeps SSR and the first client render in sync (no
    // hydration mismatch); the message/mode is applied here. The setState calls
    // below are intentional client-only initialisation.
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const msg = urlParams.get("msg");

      /* eslint-disable react-hooks/set-state-in-effect */
      if (msg === "no-token") {
        setErrorMessage("Token already used or not found.");
      }
      if (msg === ACCOUNT_STATUS.INACTIVE) {
        setErrorMessage("Your account is inactive.");
        setMode(Mode.INACTIVE);
      }
      if (msg === ACCOUNT_STATUS.TRIAL_EXPIRED) {
        setErrorMessage("Your trial has expired.");
        setMode(Mode.INACTIVE);
      }
      if (msg === ACCOUNT_STATUS.PLAN_EXPIRED) {
        setErrorMessage("Your plan has expired.");
        setMode(Mode.INACTIVE);
      }
      if (msg === ACCOUNT_STATUS.NOT_REGISTERED) {
        setErrorMessage("You do not have a registered account.");
        setMode(Mode.INACTIVE);
      }
      /* eslint-enable react-hooks/set-state-in-effect */

      // remove query string from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleLogin = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const result = await signIn("credentials", {
        email: creds.email.trim(),
        password: creds.password,
        redirect: false,
      });

      if (result?.error) {
        const accountStatusMessages = {
          [ACCOUNT_STATUS.TRIAL_EXPIRED]: "Your trial has expired.",
          [ACCOUNT_STATUS.PLAN_EXPIRED]: "Your plan has expired.",
          [ACCOUNT_STATUS.INACTIVE]: "Your account is inactive.",
          [ACCOUNT_STATUS.NOT_REGISTERED]: "You do not have a registered account.",
        };

        if (result.code && accountStatusMessages[result.code]) {
          setErrorMessage(accountStatusMessages[result.code]);
          setMode(Mode.INACTIVE);
        } else {
          setErrorMessage("Invalid credentials");
        }
        setIsLoading(false);
      } else {
        const session = await getSession();
        const status = session?.user?.accountStatus;

        if (status === ACCOUNT_STATUS.TRIAL_EXPIRED) {
          setErrorMessage("Your trial has expired.");
          setMode(Mode.INACTIVE);
          setIsLoading(false);
        } else if (status === ACCOUNT_STATUS.PLAN_EXPIRED) {
          setErrorMessage("Your plan has expired.");
          setMode(Mode.INACTIVE);
          setIsLoading(false);
        } else {
          router.push(safeCallbackUrl());
          router.refresh();
        }
      }
    } catch (error) {
      setErrorMessage("Something went wrong");
      setIsLoading(false);
    }
  };

  const handleContactSupport = () => {
    // Open email client with support email
    window.location.href = "mailto:support@floats.ai?subject=Trial expired";
  };

  const handleForgotPassword = async () => {
    if (!creds.email.trim()) {
      setErrorMessage("Please enter your email address");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await requestPasswordReset(creds.email.trim());
      setIsLoading(false);
      setSuccessMessage(
        "If an account exists for that email, a password reset link has been sent."
      );
    } catch (error) {
      setIsLoading(false);
      setErrorMessage(error.message || "Failed to send password reset email");
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Container>
        <Content>
          <Box>
            <Logo />
          </Box>
          <Form>
            <PinkPill />
            <BluePill />
            {mode !== Mode.INACTIVE && (
              <Fields>
                {errorMessage && (
                  <Alert sx={{ mb: 2, opacity: 0.8 }} severity="error">
                    {errorMessage}
                  </Alert>
                )}
                {successMessage && (
                  <Alert sx={{ mb: 2, opacity: 0.8 }} severity="success">
                    {successMessage}
                  </Alert>
                )}
                {mode === Mode.LOGIN && (
                  <>
                    <LinkedInButton
                      variant="outlined"
                      onClick={() => {
                        // Full-page navigation to the external Laravel OAuth endpoint.
                        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
                        window.location.href = `${config.apiBaseUrl}/auth/linkedin-openid/login?client=nextjs`;
                      }}
                    >
                      <LinkedInIcon />
                      Sign in with LinkedIn
                    </LinkedInButton>
                    <GoogleButton
                      variant="outlined"
                      onClick={() => {
                        // Full-page navigation to the external Laravel OAuth endpoint.
                        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
                        window.location.href = `${config.apiBaseUrl}/auth/google/login?client=nextjs`;
                      }}
                    >
                      <GoogleIcon />
                      Sign in with Google
                    </GoogleButton>

                    <OrDivider>
                      <Divider />
                      <Typography variant="body1">OR</Typography>
                      <Divider />
                    </OrDivider>

                    <FormControl sx={{ width: "100%", mb: 4 }}>
                      <Label htmlFor="login-email">Email</Label>
                      <LoginField
                        fullWidth
                        id="login-email"
                        variant="outlined"
                        autoFocus
                        value={creds.email}
                        onChange={(event) =>
                          setCreds((creds) => ({
                            ...creds,
                            email: event.target.value,
                          }))
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <Label htmlFor="login-password">Password</Label>
                      <LoginField
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        id="login-password"
                        variant="outlined"
                        value={creds.password}
                        onChange={(event) =>
                          setCreds((prev) => ({
                            ...prev,
                            password: event.target.value,
                          }))
                        }
                        endIcon={
                          <PasswordVisibilityToggle
                            visible={showPassword}
                            onToggle={() => setShowPassword((s) => !s)}
                          />
                        }
                      />
                      <Box width="100%" sx={{ textAlign: "right", mt: 1 }}>
                        <Link
                          href="#"
                          onClick={() => setMode(Mode.FORGOT_PASSWORD)}
                        >
                          Forgot password?
                        </Link>
                      </Box>
                    </FormControl>

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isLoading}
                      onClick={handleLogin}
                      sx={{ mt: 3, mb: 2 }}
                    >
                      {isLoading ? <CircularProgress size={24} /> : "Sign In"}
                    </Button>
                  </>
                )}
                {mode === Mode.FORGOT_PASSWORD && (
                  <Box sx={{ mt: 3, position: "relative" }}>
                    <FormControl sx={{ width: "100%", mb: 4 }}>
                      <Label htmlFor="forgot-password-email">Email</Label>
                      <LoginField
                        fullWidth
                        id="forgot-password-email"
                        variant="outlined"
                        autoFocus
                        value={creds.email}
                        onChange={(event) =>
                          setCreds((creds) => ({
                            ...creds,
                            email: event.target.value,
                          }))
                        }
                      />
                    </FormControl>

                    <Button
                      sx={{ width: "100%" }}
                      variant="contained"
                      disabled={isLoading}
                      onClick={handleForgotPassword}
                    >
                      Send password reset link
                    </Button>
                    {isLoading && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: "primary.main",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          marginTop: "-12px",
                          marginLeft: "-12px",
                        }}
                      />
                    )}
                  </Box>
                )}
              </Fields>
            )}
            {mode === Mode.INACTIVE && (
              <Box>
                {errorMessage && (
                  <Alert sx={{ mb: 2, opacity: 0.8 }} severity="error">
                    {errorMessage}
                  </Alert>
                )}
                <h1 className="text-primary-500">{inactiveMessage}</h1>
                <p>Please choose a plan to continue using Floats.</p>
                <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    sx={{ flex: 1 }}
                    onClick={() => {
                      setMode(Mode.LOGIN);
                      setErrorMessage("");
                      setInactiveMessage("");
                    }}
                  >
                    Back to Login
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ flex: 1 }}
                    href="/pricing"
                  >
                    Choose a Plan
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Need help?{" "}
                  <a href="mailto:support@floats.ai?subject=Trial expired" style={{ color: "inherit" }}>
                    Contact support
                  </a>
                </Typography>
              </Box>
            )}
            <Typography
              variant="body2"
              sx={{
                mt: 3,
                textAlign: "center",
                fontSize: "0.75rem",
                color: "text.secondary",
                position: "relative",
              }}
            >
              New to Floats? You can{" "}
              <Link href="/register">register here</Link>.
            </Typography>
          </Form>
          <Copyright sx={{ pt: 4 }} />
        </Content>
      </Container>
      <CookieConsent />
    </Box>
  );
}
